import React, { useEffect, useState } from "react";
import SEO from "../components/SEO";
import FAQ from "../components/FAQ";
import { joinUs } from "../api/apiClient";
import { toast } from "react-hot-toast";

const benfCards = [
  {
    icon: "/images/icons/open-an-account/benf-icon1.png",
    title: "Comprehensive Investment Options",
    desc: "Access a wide range of investment avenues including equities, mutual funds, commodities, ETFs, and fixed income instruments through a single demat account."
  },
  {
    icon: "/images/icons/open-an-account/benf-icon2.png",
    title: "Research and Advisory Services",
    desc: "Benefit from timely market insights, fundamental and technical research, and actionable recommendations to support informed investment decisions."
  },
  {
    icon: "/images/icons/open-an-account/benf-icon3.png",
    title: "Dedicated Customer Support",
    desc: "Receive personalized assistance from experienced relationship managers for account services, transactions, and investment-related queries."
  },
  {
    icon: "/images/icons/open-an-account/benf-icon4.png",
    title: "Competitive Fees",
    desc: "Enjoy transparent and cost-effective brokerage and account maintenance charges designed to enhance your overall investment returns."
  },
  {
    icon: "/images/icons/open-an-account/benf-icon5.png",
    title: "User-Friendly Digital Platforms",
    desc: "Trade seamlessly using secure and intuitive web and mobile platforms equipped with real-time data, advanced tools, and easy portfolio tracking."
  },
  {
    icon: "/images/icons/open-an-account/benf-icon6.png",
    title: "Experience The Impact of Expert Guidance",
    desc: "Leverage professional market expertise and disciplined investment strategies to build, manage, and grow your portfolio across market cycles."
  }
];

const originalBenfLength = benfCards.length;
const infiniteBenfCards = [...benfCards, ...benfCards];

const oppData = [
  {
    id: "opp1",
    icon: "/images/icons/open-an-account/folder-open.png",
    title: "Diverse Investments",
    content: "Access a wide range of investment avenues across equities, mutual funds, commodities, global markets, and fixed income instruments, enabling balanced diversification through a single integrated account."
  },
  {
    id: "opp2",
    icon: "/images/icons/open-an-account/trading.png",
    title: "Streamlined Trading",
    content: "Execute transactions smoothly with simplified processes, transparent pricing, and reliable order handling designed to support consistent investing and trading experiences."
  },
  {
    id: "opp3",
    icon: "/images/icons/open-an-account/portfolio.png",
    title: "Effortless Portfolio Tracking",
    content: "Track your holdings through consolidated portfolio views, performance updates, and timely insights that help you stay aligned with your financial goals."
  }
];

const OpenAnAccount = () => {
  // Add back the removed initial useEffect
  useEffect(() => {
    if (window.jQuery) {
      const $ = window.jQuery;
      if ($.fn.tab) {
        $(".tab1").tab();
        $(".tab2").tab({ trigger_event_type: "mouseover" });
      }
    }

    if (window.initCounterAnimation) {
      window.initCounterAnimation();
    }

    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    mobile_number: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Opportunities Accordion State
  const [oppOpenId, setOppOpenId] = useState("opp1");
  const toggleOpp = (id) => {
    setOppOpenId((prev) => (prev === id ? null : id));
  };

  // Slider State
  const [benfSliderIndex, setBenfSliderIndex] = useState(0);
  const [benfTransitionEnabled, setBenfTransitionEnabled] = useState(true);
  const [isHoveredBenf, setIsHoveredBenf] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  // Swipe State
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getCardsPerView = () => {
    if (windowWidth < 768) return 1;
    if (windowWidth < 992) return 2;
    return 3;
  };

  const cardsPerView = getCardsPerView();
  const slidePercentage = 100 / cardsPerView;

  useEffect(() => {
    if (benfSliderIndex >= originalBenfLength) {
      const timeout = setTimeout(() => {
        setBenfTransitionEnabled(false);
        setBenfSliderIndex(benfSliderIndex - originalBenfLength);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [benfSliderIndex]);

  useEffect(() => {
    if (!benfTransitionEnabled) {
      const timeout = setTimeout(() => {
        setBenfTransitionEnabled(true);
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [benfTransitionEnabled]);

  useEffect(() => {
    if (isHoveredBenf) return;
    const timer = setInterval(() => {
      setBenfSliderIndex((prev) => prev + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, [isHoveredBenf]);

  const handleBenfNext = () => setBenfSliderIndex((prev) => prev + 1);
  const handleBenfPrev = () => {
    if (benfSliderIndex === 0) {
      setBenfTransitionEnabled(false);
      setBenfSliderIndex(originalBenfLength);
      setTimeout(() => {
        setBenfTransitionEnabled(true);
        setBenfSliderIndex(originalBenfLength - 1);
      }, 50);
    } else {
      setBenfSliderIndex((prev) => prev - 1);
    }
  };

  const handleTouchStart = (e) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX - touchEndX > 50) {
      handleBenfNext();
    }
    if (touchStartX - touchEndX < -50) {
      handleBenfPrev();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length > 255) {
      newErrors.name = "Name must be less than 255 characters";
    }

    if (!formData.mobile_number.trim()) {
      newErrors.mobile_number = "Mobile number is required";
    } else {
      const phoneRegex = /^[6-9]\d{9}$/;
      if (!phoneRegex.test(formData.mobile_number.replace(/\D/g, ""))) {
        newErrors.mobile_number = "Please enter a valid 10-digit mobile number";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        mobile_number: formData.mobile_number,
      };

      const response = await joinUs(payload);

      toast.success(response?.message || "Thank you for joining us!");
      setFormData({
        name: "",
        mobile_number: "",
      });

      const link = "https://live.meon.co.in/GatewayFInancial/Individual";
      window.open(link, "_blank", "noopener noreferrer");
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="open-account-page">
      <SEO
        title="Open a Demat & Trading Account"
        description="Start your investment journey with Gateway Financial. Open a free demat account in 4 easy steps and access diverse investment opportunities with expert guidance."
      />
      <section id="banner-slider" className="inn-banner-slider">
        <div
          id="carouselExampleIndicators"
          className="carousel slide"
          data-ride="carousel"
        >
          <div className="carousel-inner" role="listbox">
            <div
              className="carousel-item active"
              style={{
                backgroundImage: windowWidth < 768 ? 'url("/images/open-an-account-banner-mobile.avif")' : 'url("/images/open-an-account-banner-desktop.avif")',
              }}
            >
              <canvas id="canvas" aria-hidden="true" />
              <div className="carousel-caption d-flex flex-column align-items-start text-left" style={{ bottom: '20%', left: windowWidth < 768 ? '15px' : '10%', right: windowWidth < 768 ? '15px' : 'auto', maxWidth: '600px' }}>
                <h1 className="mb-3" style={{ fontSize: '48px', fontWeight: 'bold', color: '#fff' }}>Join US</h1>
                <p style={{ maxWidth: '450px', fontSize: '15px', lineHeight: '1.6', marginBottom: '30px', color: '#fff' }}>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                </p>
                <form onSubmit={handleSubmit} id="oaa-contact-form" noValidate className="w-100">
                  <div className="d-flex flex-column flex-sm-row mb-4" style={{ gap: '15px' }}>
                    <div className="form-group mb-0" style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Enter your name"
                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                        name="name"
                        id="form_name"
                        value={formData.name}
                        onChange={handleChange}
                        style={{ borderRadius: '8px', padding: '12px 20px', border: 'none', height: '50px' }}
                      />
                      {errors.name && (
                        <div
                          className="invalid-feedback"
                          style={{
                            display: "block",
                            color: "#ff6b6b",
                            fontSize: "12px",
                            marginTop: "5px",
                            textAlign: "left",
                          }}
                        >
                          {errors.name}
                        </div>
                      )}
                    </div>
                    <div className="form-group mb-0" style={{ flex: 1 }}>
                      <input
                        type="tel"
                        placeholder="Enter your Mobile no."
                        className={`form-control ${errors.mobile_number ? "is-invalid" : ""}`}
                        name="mobile_number"
                        id="form_mobile"
                        value={formData.mobile_number}
                        onChange={handleChange}
                        style={{ borderRadius: '8px', padding: '12px 20px', border: 'none', height: '50px' }}
                      />
                      {errors.mobile_number && (
                        <div
                          className="invalid-feedback"
                          style={{
                            display: "block",
                            color: "#ff6b6b",
                            fontSize: "12px",
                            marginTop: "5px",
                            textAlign: "left",
                          }}
                        >
                          {errors.mobile_number}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <button
                      type="submit"
                      className="btn2 d-inline-flex align-items-center"
                      disabled={isSubmitting}
                      style={{
                        border: '1px solid #fff',
                        padding: '6px 6px 6px 20px',
                        color: '#fff',
                        gap: '15px',
                        textTransform: 'none'
                      }}
                    >
                      <span style={{ fontWeight: '500', fontSize: '16px' }}>{isSubmitting ? "Wait..." : "Next"}</span>
                      <span style={{
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="fa fa-arrow-right" style={{ color: '#004753' }}></i>
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="welcome-section" style={{ background: '#ebf3f5', paddingTop: '100px', paddingBottom: '100px' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 text-center mb-5">
              <h3 className="mb-3" data-aos="fade-up" style={{ color: '#0c3b44', fontSize: windowWidth < 768 ? '36px' : '50px', fontWeight: '700', lineHeight: '1.2' }}>
                Trade Smarter with Gateway's<br/>Stock Broking Platform
              </h3>
              <p style={{ color: '#0c3b44', fontSize: '16px', lineHeight: '1.8', margin: '0 auto', maxWidth: '800px' }}>
                Build Wealth—We've Seen 5 Market Crashes, 5 Rate Cycles, <br />
                And 10,000+ Client Portfolios.
              </p>
            </div>
          </div>
          <div className="row" style={{ marginTop: '50px' }}>
            <div className="col-lg-4 mb-5 mb-lg-0" style={{ marginTop: windowWidth < 992 ? '60px' : '0' }}>
              <div className="text-center d-flex flex-column align-items-center" style={{ background: '#fff', borderRadius: '30px', padding: '0 30px 40px 30px', height: '100%' }}>
                <div style={{
                  backgroundColor: '#fff',
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '-60px auto 30px auto',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.06)'
                }}>
                  <img
                    src="/images/icons/open-an-account/user-experience.png"
                    className="img-fluid"
                    alt="Experience Icon"
                    style={{ width: '50px' }}
                  />
                </div>
                <div className="counter-box w-100 text-center">
                  <h3 className="d-flex justify-content-center align-items-baseline mb-0" style={{ color: '#0c3b44' }}>
                    <span className="num" data-val={30}>30</span>
                    <span style={{ fontSize: '45px', fontWeight: '600' }}>+</span>
                  </h3>
                  <p style={{ color: '#0c3b44', fontSize: '14px', fontWeight: '600', marginTop: '5px', marginBottom: '0' }}>Years Experience</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-4 mb-5 mb-lg-0" style={{ marginTop: windowWidth < 992 ? '60px' : '0' }}>
              <div className="text-center d-flex flex-column align-items-center" style={{ background: '#fff', borderRadius: '30px', padding: '0 30px 40px 30px', height: '100%' }}>
                <div style={{
                  backgroundColor: '#fff',
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '-60px auto 30px auto',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.06)'
                }}>
                  <img
                    src="/images/icons/open-an-account/user-headset.png"
                    className="img-fluid"
                    alt="Customers Icon"
                    style={{ width: '50px' }}
                  />
                </div>
                <div className="counter-box w-100 text-center">
                  <h3 className="d-flex justify-content-center align-items-baseline mb-0" style={{ color: '#0c3b44' }}>
                    <span className="num" data-val={3000}>3000</span>
                    <span style={{ fontSize: '45px', fontWeight: '600' }}>+</span>
                  </h3>
                  <p style={{ color: '#0c3b44', fontSize: '14px', fontWeight: '600', marginTop: '5px', marginBottom: '0' }}>Registered Customers</p>
                </div>
              </div>
            </div>

            <div className="col-lg-4" style={{ marginTop: windowWidth < 992 ? '60px' : '0' }}>
              <div className="text-center d-flex flex-column align-items-center" style={{ background: '#fff', borderRadius: '30px', padding: '0 30px 40px 30px', height: '100%' }}>
                <div style={{
                  backgroundColor: '#fff',
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '-60px auto 30px auto',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.06)'
                }}>
                  <img
                    src="/images/icons/open-an-account/users.png"
                    className="img-fluid"
                    alt="Active Users Icon"
                    style={{ width: '50px' }}
                  />
                </div>
                <div className="counter-box w-100 text-center">
                  <h3 className="d-flex justify-content-center align-items-baseline mb-0" style={{ color: '#0c3b44' }}>
                    <span className="num" data-val={5000}>5000</span>
                    <span style={{ fontSize: '45px', fontWeight: '600' }}>+</span>
                  </h3>
                  <p style={{ color: '#0c3b44', fontSize: '14px', fontWeight: '600', marginTop: '5px', marginBottom: '0' }}>Active Users</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="checklist-section" style={{ paddingBottom: '80px' }}>
        <div className="container">
          <div className="row mb-5 justify-content-center text-center">
            <div className="col-12">
              <h3 className="mb-4" data-aos="fade-up" style={{ color: '#0c3b44', fontSize: windowWidth < 768 ? '36px' : '50px', fontWeight: '700', lineHeight: '1.2' }}>
                Documents Checklist To Open A Demat Account
              </h3>
              <a href="https://live.meon.co.in/GatewayFInancial/Individual" target="_blank" rel="noopener noreferrer" className="cu-btn cu-shadow-md px-4" style={{ textTransform: 'uppercase' }}>
                GET STARTED WITH YOUR INVESTMENT
              </a>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6 mb-4 mb-lg-0 d-flex flex-column justify-content-between">
              <div
                className="media align-items-center mb-4"
                style={{ backgroundColor: "#eff4f5", padding: "18px 25px", borderRadius: "50px" }}
              >
                <img src="/images/icons/open-an-account/id-badge.png" className="mr-3" alt="Identity Proof" style={{ width: "35px" }} />
                <div className="media-body">
                  <h5 className="mt-0 mb-0" style={{ color: '#092b34', fontSize: '17px', fontWeight: '500' }}>Identity Proof</h5>
                </div>
              </div>
              <div
                className="media align-items-center mb-4"
                style={{ backgroundColor: "#eff4f5", padding: "18px 25px", borderRadius: "50px" }}
              >
                <img src="/images/icons/open-an-account/id-card.png" className="mr-3" alt="PAN Card" style={{ width: "35px" }} />
                <div className="media-body">
                  <h5 className="mt-0 mb-0" style={{ color: '#092b34', fontSize: '17px', fontWeight: '500' }}>Pan Card</h5>
                </div>
              </div>
              <div
                className="media align-items-center mb-4"
                style={{ backgroundColor: "#eff4f5", padding: "18px 25px", borderRadius: "50px" }}
              >
                <img src="/images/icons/open-an-account/camera.png" className="mr-3" alt="Photographs" style={{ width: "35px" }} />
                <div className="media-body">
                  <h5 className="mt-0 mb-0" style={{ color: '#092b34', fontSize: '17px', fontWeight: '500' }}>Photographs &amp; Signature</h5>
                </div>
              </div>
              <div
                className="media align-items-center mb-4"
                style={{ backgroundColor: "#eff4f5", padding: "18px 25px", borderRadius: "50px" }}
              >
                <img src="/images/icons/open-an-account/address-book.png" className="mr-3" alt="Address Proof" style={{ width: "35px" }} />
                <div className="media-body">
                  <h5 className="mt-0 mb-0" style={{ color: '#092b34', fontSize: '17px', fontWeight: '500' }}>Address Proof</h5>
                </div>
              </div>
              <div
                className="media align-items-center mb-0"
                style={{ backgroundColor: "#eff4f5", padding: "18px 25px", borderRadius: "50px" }}
              >
                <img src="/images/icons/open-an-account/accountant.png" className="mr-3" alt="Bank Details" style={{ width: "35px" }} />
                <div className="media-body">
                  <h5 className="mt-0 mb-0" style={{ color: '#092b34', fontSize: '17px', fontWeight: '500' }}>Bank Account Details</h5>
                </div>
              </div>
            </div>
            <div className="col-lg-6 d-flex align-items-stretch">
              <img
                src="/images/documents-checklist.avif"
                className="w-100"
                alt="Document Checklist"
                style={{ borderRadius: "30px", objectFit: "cover", height: "100%", minHeight: "300px" }}
              />
            </div>
          </div>
        </div>
      </section>

      <section id="step-section">
        <div className="container">
          <div className="row mb-5 justify-content-center">
            <div className="col-lg-10 text-center">
              <h3 data-aos="fade-up">Get Your Free Account in 4 Easy Steps</h3>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-3 text-center mb-4 mb-lg-0">
              <div className="d-flex flex-column align-items-center h-100" style={{ backgroundColor: '#eff4f5', borderRadius: '30px', padding: '40px 20px' }}>
                <img
                  src="/images/icons/open-an-account/form.png"
                  className="img-fluid mb-4"
                  alt="Step 1"
                  style={{ width: '50px' }}
                />
                <h4 style={{ color: '#0c3b44', fontWeight: '600', fontSize: '20px', lineHeight: '1.3', marginBottom: '15px' }}>
                  Fill Out<br />The Form
                </h4>
                <p style={{ color: '#0c3b44', fontSize: '15px', lineHeight: '1.6', margin: '0' }}>
                  Complete online account form with personal financial details.
                </p>
              </div>
            </div>
            <div className="col-lg-3 text-center mb-4 mb-lg-0">
              <div className="d-flex flex-column align-items-center h-100" style={{ backgroundColor: '#eff4f5', borderRadius: '30px', padding: '40px 20px' }}>
                <img
                  src="/images/icons/open-an-account/id-badge.png"
                  className="img-fluid mb-4"
                  alt="Step 2"
                  style={{ width: '50px' }}
                />
                <h4 style={{ color: '#0c3b44', fontWeight: '600', fontSize: '20px', lineHeight: '1.3', marginBottom: '15px' }}>
                  Verify Your<br />Identity
                </h4>
                <p style={{ color: '#0c3b44', fontSize: '15px', lineHeight: '1.6', margin: '0' }}>
                  Upload PAN, Aadhaar, and bank details for KYC verification.
                </p>
              </div>
            </div>
            <div className="col-lg-3 text-center mb-4 mb-lg-0">
              <div className="d-flex flex-column align-items-center h-100" style={{ backgroundColor: '#eff4f5', borderRadius: '30px', padding: '40px 20px' }}>
                <img
                  src="/images/icons/open-an-account/memo-circle-check.png"
                  className="img-fluid mb-4"
                  alt="Step 3"
                  style={{ width: '50px' }}
                />
                <h4 style={{ color: '#0c3b44', fontWeight: '600', fontSize: '20px', lineHeight: '1.3', marginBottom: '15px' }}>
                  eSign Account<br />Opening Documents
                </h4>
                <p style={{ color: '#0c3b44', fontSize: '15px', lineHeight: '1.6', margin: '0' }}>
                  Review documents and eSign instantly using Aadhaar authentication.
                </p>
              </div>
            </div>
            <div className="col-lg-3 text-center">
              <div className="d-flex flex-column align-items-center h-100" style={{ backgroundColor: '#eff4f5', borderRadius: '30px', padding: '40px 20px' }}>
                <img
                  src="/images/icons/open-an-account/folder-open.png"
                  className="img-fluid mb-4"
                  alt="Step 4"
                  style={{ width: '50px' }}
                />
                <h4 style={{ color: '#0c3b44', fontWeight: '600', fontSize: '20px', lineHeight: '1.3', marginBottom: '15px' }}>
                  Activate Your<br />Account
                </h4>
                <p style={{ color: '#0c3b44', fontSize: '15px', lineHeight: '1.6', margin: '0' }}>
                  Once verified, your trading account is activated so you can start investing right away.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="invested-section">
        <div className="container">
          <div className="row justify-content-start">
            <div className="col-lg-12 text-center text-lg-left mb-3">
              <h3 data-aos="fade-up">Benefits of Demat Account Opening</h3>
              <p className="spc-txt text-center text-lg-left" style={{ margin: "0" }}>
                Without further ado, here's how to start.
              </p>
            </div>
          </div>
          <div className="mb-5 mx-n3" style={{ overflow: 'hidden' }}>
            <div
              className="d-flex"
              style={{
                transform: `translateX(-${benfSliderIndex * slidePercentage}%)`,
                transition: benfTransitionEnabled ? 'transform 0.5s ease-in-out' : 'none',
                width: '100%',
              }}
              onMouseEnter={() => setIsHoveredBenf(true)}
              onMouseLeave={() => setIsHoveredBenf(false)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {infiniteBenfCards.map((card, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 px-3"
                  style={{
                    flex: `0 0 ${slidePercentage}%`,
                    maxWidth: `${slidePercentage}%`,
                  }}
                >
                  <div className="invest-box text-left h-100" style={{ padding: "40px 30px", backgroundColor: "#f4f8f9", borderRadius: "15px" }}>
                    <img
                      src={card.icon}
                      className="img-fluid mb-4"
                      alt={card.title}
                      style={{ width: "45px" }}
                    />
                    <h4 style={{ fontSize: "20px", marginBottom: "15px", color: "#0c3b44", fontWeight: '700' }}>{card.title}</h4>
                    <p className="mb-0" style={{ fontSize: "15px", color: "#0c3b44", lineHeight: "1.6" }}>
                      {card.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Slider Controls */}
          <div className="row mt-4">
            <div className="col-12 d-flex justify-content-center justify-content-md-between align-items-center">
              <button 
                onClick={handleBenfPrev} 
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '50%', width: '40px', height: '40px', display: windowWidth < 768 ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer' }}
              >
                <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                </svg>
              </button>
              <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                {Array.from({ length: originalBenfLength }).map((_, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      setBenfTransitionEnabled(true);
                      setBenfSliderIndex(idx);
                    }}
                    style={{ 
                      cursor: 'pointer', 
                      width: idx === benfSliderIndex % originalBenfLength ? '24px' : '10px', 
                      height: '10px', 
                      borderRadius: '10px', 
                      backgroundColor: idx === benfSliderIndex % originalBenfLength ? '#fff' : 'rgba(255,255,255,0.3)', 
                      transition: 'all 0.3s ease' 
                    }}
                  ></div>
                ))}
              </div>
              <button 
                onClick={handleBenfNext} 
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '50%', width: '40px', height: '40px', display: windowWidth < 768 ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer' }}
              >
                <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="service-section">
        <div className="container">
          <div className="row justify-content-start">
            <div className="col-lg-12 text-center text-lg-left mb-3">
              <h3 data-aos="fade-up">One Account, Many Opportunities</h3>
              <p className="spc-txt text-center text-lg-left" style={{ margin: "0" }}>
                Without further ado, here's how to start.
              </p>
            </div>
          </div>
          <div className="row mt-4">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="accordion-wrapper mt-0">
                {oppData.map((item, index) => {
                  const isOpen = oppOpenId === item.id;
                  return (
                    <div className="accordion" key={item.id} style={{ marginBottom: '15px' }}>
                      <button
                        id={`opp-button-${item.id}`}
                        className="accordion-label"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                          textAlign: "left",
                          backgroundColor: "#f4f8f9",
                          padding: "15px 25px",
                          borderRadius: isOpen ? "25px 25px 0 0" : "50px",
                          border: "none",
                          transition: "all 0.3s ease"
                        }}
                        onClick={() => toggleOpp(item.id)}
                        aria-expanded={isOpen}
                        aria-controls={`opp-content-${item.id}`}
                      >
                        <div className="d-flex align-items-center">
                          <img
                            src={item.icon}
                            className="mr-3"
                            alt={item.title}
                            style={{ width: "30px" }}
                          />
                          <h5 className="mt-0 mb-0" style={{ fontSize: "16px", fontWeight: "500", color: "#092b34" }}>{item.title}</h5>
                        </div>
                        <span
                          className="accordion-icon"
                          aria-hidden="true"
                          style={{
                            transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                            display: "inline-block",
                            transition: "transform 0.3s ease",
                            marginLeft: "10px",
                            fontWeight: "bold",
                            color: "#092b34"
                          }}
                        >
                          &#10095;
                        </span>
                      </button>
                      <div
                        id={`opp-content-${item.id}`}
                        role="region"
                        aria-labelledby={`opp-button-${item.id}`}
                        aria-hidden={!isOpen}
                        className="accordion-content"
                        style={{
                          maxHeight: isOpen ? "500px" : "0",
                          overflow: "hidden",
                          transition: "all 0.3s ease",
                          backgroundColor: "#f4f8f9",
                          borderRadius: "0 0 25px 25px",
                          padding: isOpen ? "0 25px 20px 25px" : "0 25px",
                          opacity: isOpen ? 1 : 0
                        }}
                      >
                        <p style={{ margin: 0, fontSize: "15px", lineHeight: "1.6", color: "#444" }}>
                          {item.content}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="col-lg-6">
              <img
                src="/images/one-account-many-opportunities.avif"
                className="img-fluid w-100"
                alt="Opportunities"
                style={{ borderRadius: "15px" }}
              />
            </div>
          </div>
        </div>
      </section>

      <FAQ />

      {/* BEGIN: App Download Section */}
      <section id="app-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 align-self-center">
              <h3 data-aos="fade-up">Download our Investment Fund App</h3>
              <p>Investing With Gateway Financial Services Limited</p>
              <a href="#download" className="lm-btn">
                Download &amp; Explore More
              </a>
            </div>
            <div className="col-lg-6">
              <img
                src="/images/download.avif"
                className="img-fluid"
                alt="Download App Preview"
              />
            </div>
          </div>
        </div>
      </section>
      {/* END: App Download Section */}
    </div>
  );
};

export default OpenAnAccount;
